import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { getMyListings, deleteMyListing, toggleMyListingStatus, MyListingItem } from "@/utils/api";
import { getSiteSettings } from "@/utils/adminApi";

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

interface PinPlan {
  id: string;
  name: string;
  duration: number;
  durationUnit: string;
  price: number;
}

const pinPlans: PinPlan[] = [
  { id: "pp1", name: "24小时置顶", duration: 24, durationUnit: "小时", price: 5 },
  { id: "pp2", name: "3天置顶", duration: 3, durationUnit: "天", price: 12 },
  { id: "pp3", name: "7天置顶", duration: 7, durationUnit: "天", price: 25 },
  { id: "pp4", name: "30天置顶", duration: 30, durationUnit: "天", price: 88 },
];

interface PinModalProps {
  listingTitle: string;
  onClose: () => void;
  onConfirm: (planId: string) => void;
}

function PinModal({ listingTitle, onClose, onConfirm }: PinModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<string>(pinPlans[1].id);
  const [step, setStep] = useState<"select" | "pay" | "success">("select");
  const [payMethod, setPayMethod] = useState<"wechat" | "alipay">("wechat");

  const selected = pinPlans.find((p) => p.id === selectedPlan)!;

  const handleConfirmPay = () => {
    setStep("success");
    setTimeout(() => {
      onConfirm(selectedPlan);
      onClose();
    }, 1500);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-pushpin-fill text-orange-500 text-base" />
            </div>
            <h3 className="font-semibold text-gray-800 text-sm">
              {step === "select" ? "选择置顶套餐" : step === "pay" ? "确认支付" : "支付成功"}
            </h3>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500 text-sm" />
          </button>
        </div>

        {step === "select" && (
          <>
            <div className="px-5 pt-4 pb-2">
              <p className="text-xs text-gray-500 mb-4">
                为 <span className="font-medium text-gray-700 line-clamp-1">「{listingTitle}」</span> 选择置顶时长
              </p>
              <div className="grid grid-cols-2 gap-2.5">
                {pinPlans.map((plan) => (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`relative p-3.5 rounded-xl border-2 text-left transition-all cursor-pointer ${
                      selectedPlan === plan.id
                        ? "border-orange-400 bg-orange-50"
                        : "border-gray-100 bg-gray-50 hover:border-orange-200"
                    }`}
                  >
                    {selectedPlan === plan.id && (
                      <div className="absolute top-2 right-2 w-4 h-4 flex items-center justify-center bg-orange-500 rounded-full">
                        <i className="ri-check-line text-white text-xs" />
                      </div>
                    )}
                    <div className="text-sm font-semibold text-gray-800">{plan.name}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{plan.duration}{plan.durationUnit}</div>
                    <div className="mt-2">
                      <span className="text-lg font-bold text-orange-500">¥{plan.price}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                已选：<span className="font-semibold text-gray-800">{selected.name}</span>
                <span className="text-orange-500 font-bold ml-2">¥{selected.price}</span>
              </div>
              <button
                onClick={() => setStep("pay")}
                className="px-5 py-2 bg-orange-500 text-white text-sm font-medium rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                立即支付
              </button>
            </div>
          </>
        )}

        {step === "pay" && (
          <>
            <div className="px-5 py-5 space-y-4">
              <div className="bg-orange-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">置顶套餐</span>
                  <span className="text-sm font-medium text-gray-800">{selected.name}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">置顶时长</span>
                  <span className="text-sm text-gray-700">{selected.duration}{selected.durationUnit}</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-orange-100">
                  <span className="text-sm font-semibold text-gray-700">应付金额</span>
                  <span className="text-xl font-bold text-orange-500">¥{selected.price}</span>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-2.5">选择支付方式</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setPayMethod("wechat")}
                    className={`flex-1 flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                      payMethod === "wechat" ? "border-green-400 bg-green-50" : "border-gray-100 hover:border-green-200"
                    }`}
                  >
                    <div className="w-7 h-7 flex items-center justify-center bg-green-500 rounded-lg shrink-0">
                      <i className="ri-wechat-pay-fill text-white text-base" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">微信支付</span>
                    {payMethod === "wechat" && <i className="ri-check-line text-green-500 ml-auto text-sm" />}
                  </button>
                  <button
                    onClick={() => setPayMethod("alipay")}
                    className={`flex-1 flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                      payMethod === "alipay" ? "border-sky-400 bg-sky-50" : "border-gray-100 hover:border-sky-200"
                    }`}
                  >
                    <div className="w-7 h-7 flex items-center justify-center bg-sky-500 rounded-lg shrink-0">
                      <i className="ri-alipay-fill text-white text-base" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">支付宝</span>
                    {payMethod === "alipay" && <i className="ri-check-line text-sky-500 ml-auto text-sm" />}
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-400 text-center">支付即视为同意置顶服务条款，置顶到期后自动失效</p>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex gap-3">
              <button onClick={() => setStep("select")} className="flex-1 py-2.5 text-sm border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer">
                返回
              </button>
              <button
                onClick={handleConfirmPay}
                className="flex-1 py-2.5 bg-orange-500 text-white text-sm font-medium rounded-xl hover:bg-orange-600 transition-colors cursor-pointer"
              >
                确认支付 ¥{selected.price}
              </button>
            </div>
          </>
        )}

        {step === "success" && (
          <div className="px-5 py-10 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-green-100 rounded-full">
              <i className="ri-checkbox-circle-fill text-green-500 text-3xl" />
            </div>
            <h4 className="text-base font-semibold text-gray-800 mb-1">支付成功！</h4>
            <p className="text-sm text-gray-500">置顶已生效，有效期 {selected.duration}{selected.durationUnit}</p>
          </div>
        )}
      </div>
    </div>
  );
}

type AuditTab = "all" | "pending" | "reviewed";

const auditTabConfig: { key: AuditTab; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "pending", label: "待审核" },
  { key: "reviewed", label: "已审核" },
];

export default function MyListingsPage() {
  const navigate = useNavigate();
  const [listings, setListings] = useState<MyListingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<AuditTab>("all");
  const [pinModalListing, setPinModalListing] = useState<{ id: string; title: string } | null>(null);
  const [expandedReject, setExpandedReject] = useState<string | null>(null);
  const [enableTopping, setEnableTopping] = useState(false);

  // 加载数据
  const loadListings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getMyListings({ page_size: 100 });
      setListings(response.list || []);
    } catch (error) {
      console.error('加载我的发布失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadListings();
    // 加载置顶功能开关
    getSiteSettings().then((settings) => {
      setEnableTopping(settings.enable_topping ?? false);
    }).catch(() => {});
  }, [loadListings]);

  // 根据 Tab 筛选
  const filteredListings = listings.filter((l) => {
    if (activeTab === "all") return true;
    if (activeTab === "pending") return l.audit_status === "pending";
    if (activeTab === "reviewed") return l.audit_status === "active" || l.audit_status === "rejected" || l.audit_status === "offline";
    return true;
  });

  // 统计各状态数量
  const counts = {
    all: listings.length,
    pending: listings.filter((l) => l.audit_status === "pending").length,
    reviewed: listings.filter((l) => l.audit_status === "active" || l.audit_status === "rejected" || l.audit_status === "offline").length,
  };

  // 删除
  const handleDelete = async (id: string) => {
    if (!confirm("确定要删除这条信息吗？此操作不可恢复。")) return;
    try {
      await deleteMyListing(id);
      setListings((prev) => prev.filter((l) => l.id !== id));
    } catch (error) {
      alert("删除失败，请重试");
    }
  };

  // 上下架
  const handleToggleStatus = async (id: string) => {
    try {
      const result = await toggleMyListingStatus(id);
      setListings((prev) =>
        prev.map((l) =>
          l.id === id ? { ...l, status: result.status as MyListingItem['status'], audit_status: result.status as MyListingItem['audit_status'] } : l
        )
      );
    } catch (error) {
      alert("操作失败，请重试");
    }
  };

  // 置顶确认（暂时模拟）
  const handleConfirmPin = (_planId: string) => {
    // TODO: 调用置顶支付接口
    if (!pinModalListing) return;
    setListings((prev) =>
      prev.map((l) => (l.id === pinModalListing.id ? { ...l, is_pinned: true } : l))
    );
    setPinModalListing(null);
  };

  // 取消置顶
  const handleCancelPin = (id: string) => {
    setListings((prev) =>
      prev.map((l) => (l.id === id ? { ...l, is_pinned: false } : l))
    );
  };

  const getAuditBadge = (status: string) => {
    if (status === "pending") {
      return <span className="px-2 py-0.5 bg-yellow-50 text-yellow-600 text-xs rounded-full">待审核</span>;
    }
    if (status === "active") {
      return <span className="px-2 py-0.5 bg-green-50 text-green-600 text-xs rounded-full">已通过</span>;
    }
    if (status === "rejected") {
      return <span className="px-2 py-0.5 bg-red-50 text-red-500 text-xs rounded-full">已驳回</span>;
    }
    if (status === "offline") {
      return <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">已下架</span>;
    }
    return null;
  };

  return (
    <DashboardLayout>
      <div className="p-5 md:p-6">
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-lg md:text-xl font-bold text-gray-800">我的发布</h1>
          <Link
            to="/publish"
            className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors text-sm whitespace-nowrap"
          >
            <i className="ri-add-line" />
            发布新信息
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-5 w-fit">
          {auditTabConfig.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? "bg-white text-gray-800 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.key ? "bg-orange-100 text-orange-600" : "bg-gray-200 text-gray-500"
              }`}>
                {counts[tab.key]}
              </span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full"></div>
            <span className="ml-3 text-gray-500">加载中...</span>
          </div>
        ) : filteredListings.length > 0 ? (
          <div className="space-y-3">
            {filteredListings.map((listing) => (
              <div key={listing.id} className="rounded-xl border border-gray-100 overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                      {listing.images && listing.images[0] ? (
                        <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-cover object-top" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                          <i className="ri-image-line text-lg" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link
                          to={`/listing/${listing.id}`}
                          className="text-sm font-medium text-gray-800 hover:text-orange-600 transition-colors line-clamp-1"
                        >
                          {listing.title}
                        </Link>
                        {listing.is_pinned && (
                          <span className="px-1.5 py-0.5 bg-orange-100 text-orange-600 text-xs rounded">置顶中</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {listing.category_name && (
                          <span className="px-2 py-0.5 bg-orange-50 text-orange-600 text-xs rounded-full">{listing.category_name}</span>
                        )}
                        <span className="text-xs text-gray-400">{formatDate(listing.created_at)}</span>
                        <span className="text-xs text-gray-400">{listing.view_count} 次浏览</span>
                        {getAuditBadge(listing.audit_status)}
                        {listing.audit_status === "active" && (
                          <span className={`px-2 py-0.5 text-xs rounded-full ${listing.status === "active" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                            {listing.status === "active" ? "已发布" : "已下架"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 驳回原因提示 */}
                  {listing.audit_status === "rejected" && (
                    <div className="mt-3">
                      <button
                        onClick={() => setExpandedReject(expandedReject === listing.id ? null : listing.id)}
                        className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-600 cursor-pointer"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-error-warning-line" />
                        </div>
                        查看驳回原因
                        <div className="w-4 h-4 flex items-center justify-center">
                          {expandedReject === listing.id ? <i className="ri-arrow-up-s-line" /> : <i className="ri-arrow-down-s-line" />}
                        </div>
                      </button>
                      {expandedReject === listing.id && (
                        <div className="mt-2 p-3 bg-red-50 rounded-lg border border-red-100">
                          <p className="text-xs text-red-600 leading-relaxed">信息内容不符合平台规范，请修改后重新提交。</p>
                          <p className="text-xs text-red-400 mt-1.5">请根据以上意见修改后重新提交审核</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 待审核提示 */}
                  {listing.audit_status === "pending" && (
                    <div className="mt-3 flex items-center gap-1.5 text-xs text-yellow-600">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-time-line" />
                      </div>
                      信息正在审核中，预计 1-2 个工作日内完成
                    </div>
                  )}
                </div>

                {/* 操作栏 */}
                <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-t border-gray-100">
                  {listing.audit_status !== "pending" && (
                    <button
                      onClick={() => navigate(`/dashboard/edit-listing/${listing.id}`)}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-edit-line" />
                      编辑
                    </button>
                  )}
                  {(listing.audit_status === "active" || listing.audit_status === "offline") && (
                    <button
                      onClick={() => handleToggleStatus(listing.id)}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className={`ri-${listing.status === "active" ? "eye-off-line" : "eye-line"}`} />
                      {listing.status === "active" ? "下架" : "上架"}
                    </button>
                  )}
                  {listing.audit_status === "active" && (
                    listing.is_pinned ? (
                      <button
                        onClick={() => handleCancelPin(listing.id)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs text-orange-600 bg-orange-50 rounded-lg transition-colors hover:bg-orange-100 cursor-pointer"
                      >
                        <i className="ri-pushpin-fill" />
                        取消置顶
                      </button>
                    ) : enableTopping && (
                      <button
                        onClick={() => setPinModalListing({ id: listing.id, title: listing.title })}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer"
                      >
                        <i className="ri-pushpin-line" />
                        置顶
                      </button>
                    )
                  )}
                  {listing.audit_status === "rejected" && (
                    <button
                      onClick={() => navigate(`/dashboard/edit-listing/${listing.id}`)}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-refresh-line" />
                      修改重投
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(listing.id)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-auto cursor-pointer"
                  >
                    <i className="ri-delete-bin-line" />
                    删除
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-gray-100">
              <i className="ri-file-list-line text-2xl text-gray-400" />
            </div>
            <p className="text-gray-500 text-sm">
              {activeTab === "pending" ? "暂无待审核的信息" : activeTab === "reviewed" ? "暂无已审核的信息" : "您还没有发布任何信息"}
            </p>
            {activeTab === "all" && (
              <Link
                to="/publish"
                className="inline-flex items-center mt-4 px-5 py-2.5 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors text-sm"
              >
                立即发布
              </Link>
            )}
          </div>
        )}
      </div>

      {pinModalListing && (
        <PinModal
          listingTitle={pinModalListing.title}
          onClose={() => setPinModalListing(null)}
          onConfirm={handleConfirmPin}
        />
      )}
    </DashboardLayout>
  );
}
